import { useMemo } from 'react';
import { createFileRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useHasPermission } from '@/hooks/usePermissions';
import { BookingListPanel } from '@/components/organisms/bookings/BookingListPanel';
import { BookingTableView } from '@/components/organisms/bookings/BookingTableView';
import { SoldWorkTriageTable } from '@/components/organisms/bookings/SoldWorkTriageTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { EntityIndexLayout, JobsWorkspaceShell, useWorkspaceHeader } from '@/components/layouts';

export const Route = createFileRoute('/_dashboard/dashboard/bookings')({
  component: BookingsLayout,
});

function BookingsLayout() {
  const { location } = useRouterState();
  const router = useRouter();

  const idMatch = location.pathname.match(/\/dashboard\/bookings\/([^/]+)/);
  const selectedId = idMatch && idMatch[1] !== 'new' ? idMatch[1] : undefined;
  const isFormMode = location.pathname.includes('/dashboard/bookings/new');
  const hasChildRoute = !!selectedId || isFormMode;
  const canViewSoldWork = useHasPermission('estimates:view');

  function handleSelect(id: string) {
    void router.navigate({ to: '/dashboard/bookings/$bookingId', params: { bookingId: id } });
  }

  return (
    <JobsWorkspaceShell>
      <BookingsNewButtonSlot hidden={hasChildRoute} />
      <EntityIndexLayout
        title="Requests / Needs triage"
        storageKey="bookings-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() => void router.navigate({ to: '/dashboard/bookings' })}
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        // NOT-556: drop the List (split master-detail) view — the requests index
        // is only the Table, and both the New form and a selected request open
        // full-page in the table area (like a visit/appointment), never in a
        // slide-over sheet.
        disableSplitView
        fullPageFormInTable
        hideListInSplit={hasChildRoute}
        tableView={() =>
          canViewSoldWork ? (
            <Tabs defaultValue="requests" className="min-h-0 flex-1 px-4 pt-3">
              <TabsList>
                <TabsTrigger value="requests">Requests</TabsTrigger>
                <TabsTrigger value="sold-work">Sold work</TabsTrigger>
              </TabsList>
              <TabsContent value="requests" className="min-h-0 pt-2">
                <BookingTableView
                  onOpen={(id) =>
                    void router.navigate({
                      to: '/dashboard/bookings/$bookingId',
                      params: { bookingId: id },
                    })
                  }
                />
              </TabsContent>
              <TabsContent value="sold-work" className="min-h-0 pt-2">
                <SoldWorkTriageTable />
              </TabsContent>
            </Tabs>
          ) : (
            <BookingTableView
              onOpen={(id) =>
                void router.navigate({
                  to: '/dashboard/bookings/$bookingId',
                  params: { bookingId: id },
                })
              }
            />
          )
        }
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden flex flex-col flex-1 min-h-0">
            {hasChildRoute ? (
              <Outlet />
            ) : canViewSoldWork ? (
              <Tabs defaultValue="requests" className="min-h-0 flex-1 px-3 pt-3">
                <TabsList>
                  <TabsTrigger value="requests">Requests</TabsTrigger>
                  <TabsTrigger value="sold-work">Sold work</TabsTrigger>
                </TabsList>
                <TabsContent value="requests" className="min-h-0 pt-2">
                  <BookingListPanel selectedId={selectedId} onSelect={handleSelect} />
                </TabsContent>
                <TabsContent value="sold-work" className="min-h-0 pt-2">
                  <SoldWorkTriageTable />
                </TabsContent>
              </Tabs>
            ) : (
              <BookingListPanel selectedId={selectedId} onSelect={handleSelect} />
            )}
          </div>
        }
      />
    </JobsWorkspaceShell>
  );
}

function BookingsNewButtonSlot({ hidden }: { hidden: boolean }) {
  const canCreateRequests = useHasPermission('bookings:create');
  const newButton = useMemo(() => {
    if (hidden || !canCreateRequests) return null;
    return (
      <Button size="sm" title="New request" asChild>
        <Link to="/dashboard/bookings/new" search={{ returnTo: 'requests' }}>
          <Plus size={14} />
          New request
        </Link>
      </Button>
    );
  }, [canCreateRequests, hidden]);

  useWorkspaceHeader({ newButton }, [newButton]);
  return null;
}
